/**
 * Schema-driven list presentation helpers for RuntimeListView.
 *
 * Bridges the Designer's FormSchema (kind='list', root Table node) to the
 * lightweight ListColumnSpec the runtime grid + shared cell renderer consume.
 * When a schema (or a Table node with columns) is absent, `columnsFromAttributes`
 * reproduces the exact attribute-derived column list RuntimeListView used before
 * schema support existed — same dedupe / skip rules, same order.
 */
import type { FormNode, FormSchema, ListFormFilterField } from '@/designer/types/form.types'
import type { AttributeDef } from '@/designer/services/configObjectApi'
import { hasListCrudCommandBar, isListSearchField } from '@/shared/FormRenderer/listFormActions'
import type { ListTableColumn } from '@/shared/listTable/types'
import { widthSx as sharedWidthSx } from '@/shared/listTable/widthSx'
import { isPlatformInternalAttr, isAppHiddenAttr } from './platformInternalAttrs'

/** @deprecated Prefer ListTableColumn from `@/shared/listTable` — kept as alias for runtime callers. */
export type ListColumnSpec = ListTableColumn

/** Maps a column's width (string | number | undefined) to sx props for the list table. */
export function widthSx(width: ListColumnSpec['width']): { width?: string | number; maxWidth?: string | number } {
  return sharedWidthSx(width)
}

/** Depth-first search for the first Table node with a non-empty columns array. */
export function findTableNode(root: FormNode[]): FormNode | null {
  for (const node of root ?? []) {
    if (node.type === 'Table' && node.columns && node.columns.length > 0) return node
    if (node.children?.length) {
      const found = findTableNode(node.children)
      if (found) return found
    }
  }
  return null
}

/** Maps the schema's Table columns to ListColumnSpec, or null when no such Table exists. */
export function columnsFromSchema(schema: FormSchema | null | undefined): ListColumnSpec[] | null {
  if (!schema) return null
  const table = findTableNode(schema.root ?? [])
  if (!table?.columns?.length) return null
  return table.columns
    .filter((c) => !isPlatformInternalAttr(c.name) && !(c.dataPath != null && isPlatformInternalAttr(c.dataPath)))
    .map((c) => ({
      name: c.dataPath?.replace('Object.', '') || c.name,
      title: c.title || c.name,
      synonyms: c.synonyms,
      width: c.width,
      format: c.format,
      chip: c.chip,
      progress: c.progress,
      mono: c.mono,
      colorField: c.colorField,
    }))
}

/**
 * Merges the schema's `data_query.filters` (lowest priority), static `list_filter`,
 * the active tab's `filter`, and the user-picked `filter_fields` values into a
 * single equality-filter map for the server `filters` query param. Later sources
 * win on key collision (data_query < list_filter < tab filter < user filter).
 * Empty-string / nullish values are dropped so a cleared select doesn't send an
 * empty-string equality filter.
 */
export function buildListFilters(
  schema: Pick<FormSchema, 'data_query' | 'list_filter' | 'tabs'> | null | undefined,
  tabIndex: number,
  userFilters: Record<string, string>,
): Record<string, string | number | boolean> {
  const merged: Record<string, string | number | boolean> = {}
  const put = (src?: Record<string, string | number | boolean> | undefined) => {
    for (const [k, v] of Object.entries(src ?? {})) if (v !== '' && v != null) merged[k] = v
  }
  // data_query.filters is the lowest priority
  put(Object.fromEntries((schema?.data_query?.filters ?? []).map((f) => [f.field, f.value])))
  put(schema?.list_filter)
  put(schema?.tabs?.[tabIndex]?.filter)
  put(userFilters)
  return merged
}

export interface ListCommandBarSpec {
  /** Schema declares its own CRUD command bar → toolbar shows Create/Edit/Delete per schema. */
  hasCrudBar: boolean
  /** Schema declares a search field. */
  hasSearch: boolean
}

/**
 * Bridges a list FormSchema to the default `ListToolbar`: when the schema declares
 * its own CommandBar (Create button) and/or ListSearch field, the generic toolbar
 * defers to it instead of rendering its own Create button / search box. Without a
 * schema, or when the schema has no CommandBar node at all (stale schemas that only
 * generated a Table), the toolbar fails open and renders its own Create + search —
 * a CommandBar-less list must never lose its Create action.
 */
export function commandBarFromSchema(schema: FormSchema | null | undefined): ListCommandBarSpec {
  if (!schema) return { hasCrudBar: true, hasSearch: true }
  const root = schema.root ?? []
  const hasCommandBar = root.some((n) => n.type === 'CommandBar')
  if (!hasCommandBar) return { hasCrudBar: true, hasSearch: true }
  const nodes: FormNode[] = []
  const walk = (list: FormNode[]) => list.forEach((n) => { nodes.push(n); if (n.children?.length) walk(n.children) })
  walk(root)
  return {
    hasCrudBar: hasListCrudCommandBar(root),
    hasSearch: nodes.some((n) => isListSearchField(n)),
  }
}

/**
 * Derives column specs from attributes, with optional field filtering.
 * Current visibleColumns logic: dedupe by lower-cased name, skip tabular attrs and platform-internal fields.
 * When `onlyFields` is provided, only include attributes whose names appear in that list.
 */
export function columnsFromAttributes(attrs: AttributeDef[], onlyFields?: string[]): ListColumnSpec[] {
  const seen = new Set<string>()
  const out: ListColumnSpec[] = []
  const fieldSet = onlyFields ? new Set(onlyFields.map((f) => f.toLowerCase())) : null
  for (const a of attrs ?? []) {
    if (a.attr_type === 'tabular' || isAppHiddenAttr(a)) continue
    const key = a.name.toLowerCase()
    if (seen.has(key)) continue
    // If onlyFields is specified, skip attributes not in the list
    if (fieldSet && !fieldSet.has(key)) continue
    seen.add(key)
    out.push({ name: a.name, title: a.synonym || a.name, synonyms: a.synonyms })
  }
  return out
}

/**
 * Returns the default sort order from schema's data_query.order, or null if not specified.
 * Uses the first order entry if multiple are present.
 */
export function defaultSortFromSchema(schema: FormSchema | null | undefined): { field: string; dir: 'asc' | 'desc' } | null {
  const order = schema?.data_query?.order?.[0]
  if (!order) return null
  return { field: order.field, dir: order.dir }
}

/**
 * Filter-field descriptors for composite (COMPOSITE_REF) attributes: one
 * `{name}_type` type-select per composite attr, static options from
 * `props.composite_types` (no `options_endpoint` — the set is small and
 * known up front). RuntimeListView merges these with any schema-declared
 * `filter_fields` and renders an adjacent per-target value picker for a
 * `_type` field it recognizes as composite (Task 8).
 */
export function compositeFilterFields(attrs: AttributeDef[] | null | undefined): ListFormFilterField[] {
  const out: ListFormFilterField[] = []
  for (const a of attrs ?? []) {
    if (a.attr_type !== 'composite_ref') continue
    const types = Array.isArray(a.props?.composite_types) ? a.props.composite_types.map(String) : []
    if (!types.length) continue
    out.push({
      name: `${a.name}_type`,
      title: `${a.synonym || a.name} type`,
      type: 'string',
      options: types.map((t) => ({ value: t, label: t })),
    })
  }
  return out
}
